'use client';

import { scrollToViewPromise } from '@cc-shopping/utils/scrollToViewPromise';
import { Box, ButtonBase, Dialog, List } from '@mui/material';
import { type MouseEvent, startTransition, useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Link } from './components/Link';
import { SectionName } from './components/SectionName';
import { Title } from './components/Title';
import { useHeadingsObserver } from './hooks/useHeadingsObserver';
import { useTableOfContents } from './hooks/useTableOfContents';
import type { TableOfContentsHeadingProps } from './types';

type Props = {
  headings: TableOfContentsHeadingProps[];
  tocId: string;
};

export function Mobile({ headings, tocId }: Props) {
  const [isDialogVisible, setDialogVisible] = useState(false);
  const { activeId, setActiveId, fireNavigateToSectionPageAction } = useTableOfContents({
    headings,
  });

  const { pauseScrollTracking, reenableScrollTracking } = useHeadingsObserver({
    activeId,
    headings,
    setActiveId,
    tocId,
  });

  const viewDetailsOnClick = () => {
    startTransition(() => {
      setDialogVisible(true);
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement) {
        activeElement.blur();
      }
    });
  };

  const close = () => {
    setDialogVisible(false);
  };

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      close();
      const href = event.currentTarget.href;
      const headingId = href.substring(href.indexOf('#') + 1);
      const headingContent = event.currentTarget.innerText;

      pauseScrollTracking();
      startTransition(() => {
        setActiveId(headingId);
      });

      fireNavigateToSectionPageAction({
        action_input: `${headings.findIndex(element => element.id === headingId)}`,
        action_location_url: window.location.href,
        activity_id: uuid(),
        additional_attributes: {
          toc_section: headingContent,
        },
      });

      await scrollToViewPromise(headingId, 'smooth', 120, true);

      reenableScrollTracking();
    },
    [JSON.stringify(headings)],
  );

  const activeHeader = headings.find(heading => heading.id === activeId)?.header;

  return (
    <Box
      id={tocId}
      sx={{
        display: { xs: 'block', md: 'none' },
        backgroundColor: 'white',
        width: '100vw',
        position: 'relative',
        left: { xs: '-1rem', sm: 'calc(var(--padding-x) * -1)' },
        borderBottom: '1px solid #D8D9DA',
        boxShadow: '0px 8px 8px 0px #64666A26',
      }}
    >
      <ButtonBase
        onClick={viewDetailsOnClick}
        disableRipple
        sx={{
          display: 'block',
          width: '100%',
          paddingTop: 3,
          '&:hover': { backgroundColor: 'neutral.lightest' },
          '&:active': {
            outline: '2px solid',
            outlineOffset: '-2px',
            outlineColor: 'secondary.main',
            outlineStyle: 'auto',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingX: 3,
            marginBottom: 2,
          }}
        >
          <Title />
          <Box
            component="span"
            aria-hidden="true"
            className="mask-icon mask-icon-expand-more"
            data-testid="toc-caret-icon"
            sx={{
              width: '24px',
              height: '24px',
            }}
          />
        </Box>
        {activeHeader && (
          <Box
            component="span"
            sx={{
              display: 'block',
              paddingY: 2,
              paddingLeft: 3,
              backgroundColor: 'blues.lightest',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <SectionName bold>{activeHeader}</SectionName>
          </Box>
        )}
      </ButtonBase>
      <Dialog
        component="nav"
        aria-label="Table of contents"
        open={isDialogVisible}
        onClose={close}
        fullScreen
        PaperProps={{
          sx: {
            borderWidth: '1px',
            borderStyle: 'solid',
            marginTop: { xs: '20vh', sm: '45vh' },
            minWidth: '100%',
            borderRadius: '10px 10px 0px 0px',
            padding: 3,
            height: { xs: '80vh', sm: '55vh' },
          },
        }}
      >
        <Box
          padding={3}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
          }}
        >
          <Title id="toc-title-mobile" />
          <ButtonBase disableRipple onClick={close}>
            <span
              aria-hidden="true"
              className="mask-icon mask-icon-close"
              style={{ width: '24px', height: '24px' }}
              data-testid="toc-cross-icon"
            />
          </ButtonBase>
        </Box>
        <List
          component="ol"
          sx={{ padding: 2, overflow: 'auto' }}
          aria-labelledby="toc-title-mobile"
        >
          {headings.map(heading => (
            <Box component="li" key={heading.id} sx={{ padding: 0 }}>
              <Link
                isSelected={activeId === heading.id}
                headingId={heading.id}
                onClick={handleClick}
                {...(heading.id === activeId && {
                  className: 'selected',
                })}
              >
                {heading.header}
              </Link>
            </Box>
          ))}
        </List>
      </Dialog>
    </Box>
  );
}
